import { useFormContext, useWatch } from "react-hook-form";
import QuotationHeader from "../../../components/Form/Sales/Quotation/Header";
import CustomerInfo from "../../../components/Form/Sales/CustomerInfo";
import AddressInfo from "../../../components/Form/Sales/CustomerInfo/AddressInfo";
import SalesInfo from "../../../components/Form/Sales/SalesInfo";
import SalesItemList from "../../../components/Table/Sales/ItemList/SalesItemList";
import SalesFooter from "../../../components/Form/Sales/Footer";
import { CustomizedBox } from "../../../components/Custom/CustomizedBox";
import AcceptQuotationForm from "../../../components/Form/Sales/Quotation/Accept";
import BottomNavbar from "../../../components/UI/Navbar/BottomNavbar";
import ModalUI from "../../../components/UI/Modal/ModalUI";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import { Box, CircularProgress, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDisable } from "../../../hooks/use-disable";
import {
  quotationCreatePayloadFormatter,
  quotationQueryFormatter,
  quotationUpdatePayloadFormatter,
} from "../../../utils/Formatter/Sales/Quotation";
import { IQuotation } from "../../../types/Sales/quotation";
import {
  QuotationCreateInput,
  QuotationFindUniqueQuery,
  QuotationUpdateInput,
  useQuotationCreateMutation,
  useQuotationFindUniqueQuery,
  useQuotationUpdateMutation,
} from "../../../generated/sales";
import { useSnackbar } from "notistack";
import { useActivityLog } from "../../../hooks/use-activity-log";
import { useEffect, useState } from "react";
import { createGraphQLClientWithMiddleware } from "../../../services/graphqlClient";
import { useSalesOption } from "../../../hooks/Sales/use-sales-option";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSalesError } from "../../../hooks/Sales/use-sales-error";
import { useConfirmation } from "../../../hooks/use-confirmation";
import {
  ActivityLogDocumentType,
  ActivityType,
} from "../../../generated/general";
import { IApprovalTemplate } from "../../../types/global";
import { useStateContext } from "../../../contexts/auth-context";
import { errorMessageFormatter } from "../../../utils/Global";
import dayjs from "dayjs";

const DocumentInfoTab = () => {
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [disabled, setDisabled] = useDisable();
  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { createActivityLog } = useActivityLog();
  const { isLoading: isOptionLoading, approvalTemplates } =
    useSalesOption("quotation");
  const { state } = useLocation();
  const { id } = useParams();
  const {
    state: { authUser, permissions },
  } = useStateContext();

  const navigate = useNavigate();
  const [approverList, setApproverList] = useState<IApprovalTemplate[]>([]);
  const [approvalStep, setApprovalStep] = useState<number>(0);
  const [isHaveApproval, setIsHaveApproval] = useState<boolean>(false);
  const isApproval = approvalTemplates && approvalTemplates.length > 0;

  const [netAmount, setNetAmount] = useState<number | null | undefined>(0);

  const {
    control,
    getValues,
    reset,
    trigger,
    handleSubmit,
    formState: { dirtyFields, errors, isValid },
  } = useFormContext<IQuotation>();

  const graphQLClient = createGraphQLClientWithMiddleware("sales");

  const status = useWatch({
    control,
    name: "aggrid_status",
  });

  const { data, isLoading, isSuccess, refetch } =
    useQuotationFindUniqueQuery<QuotationFindUniqueQuery>(
      graphQLClient,
      {
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
      },
      {
        enabled: !!id,
        cacheTime: 0,
      }
    );

  useEffect(() => {
    setIsLoadingData(true);
    if (isSuccess) {
      const { QuotationFindUnique } = data;
      setNetAmount(QuotationFindUnique.net_amount);

      const getQuotationData = async () => {
        const formattedQuotation = await quotationQueryFormatter(
          QuotationFindUnique
        );
        reset(formattedQuotation);
        setIsLoadingData(false);
      };
      getQuotationData();
    }
  }, [data, isSuccess, reset]);

  useEffect(() => {
    if (isSuccess) {
      const { QuotationFindUnique } = data;
      const currentStep = QuotationFindUnique?.approval_step as number;
      setApprovalStep(currentStep);
      if (QuotationFindUnique?.main_status === "wait_approve") {
        setApproverList(approvalTemplates as IApprovalTemplate[]);
      } else {
        const allApproval = QuotationFindUnique?.approver_list || [];
        setApproverList(allApproval);
      }

      const currentReviewerList =
        approvalTemplates.find(
          (template) => template?.step_number === currentStep
        )?.approver_list || [];

      const isHaveApproval = currentReviewerList.some(
        (reviewer) => reviewer.id === authUser?.id
      );

      setIsHaveApproval(isHaveApproval);
    }
  }, [approvalTemplates, authUser?.id, data, isSuccess]);

  useEffect(() => {
    if (
      id &&
      ((status !== "draft" && status !== "not_approved") ||
        !permissions?.quotation?.update)
    ) {
      setDisabled(true);
    }

    return () => {
      setDisabled(false);
    };
  }, [id, permissions?.quotation?.update, setDisabled, status]);

  useEffect(() => {
    if (state) {
      const { copied_unique_id, copied_id, ...otherState } = state;
      reset({
        ...otherState,
        created_date: dayjs(),
        issue_date: dayjs(),
        due_date: dayjs().add(state.credit_day || 0, "days"),
      });
    }
  }, [reset, state]);

  const { mutateAsync: create, isLoading: isCreating } =
    useQuotationCreateMutation<Error>(graphQLClient);

  const { mutateAsync: update, isLoading: isUpdating } =
    useQuotationUpdateMutation<Error>(graphQLClient);

  const draftHandler = async (data: IQuotation) => {
    if (!id) {
      try {
        const formatData = quotationCreatePayloadFormatter(data, "draft");
        const { QuotationCreate } = await create({
          input: formatData as QuotationCreateInput,
        });
        enqueueSnackbar(`${t("button.save_draft")}สำเร็จ`, {
          variant: "success",
        });
        navigate(`/sales/quotation/${QuotationCreate.id}`);
        if (state && state.copied_id) {
          await createActivityLog({
            activity_type: ActivityType.Copy,
            document_type: ActivityLogDocumentType.Quotation,
            reference_id: QuotationCreate?.id,
            activity_detail: {
              copied_from: {
                id: state.copied_id,
                unique_id: state.copied_unique_id,
              },
              copied_to: {
                id: QuotationCreate.id,
                unique_id: QuotationCreate.unique_id,
              },
            },
          });
        }
        await createActivityLog({
          activity_type: ActivityType.StatusChange,
          document_type: ActivityLogDocumentType.Quotation,
          reference_id: QuotationCreate?.id,
          activity_detail: {
            secondary_operation: ActivityType.Create,
            curr_status: "draft",
            updated_fields: {
              net_amount: QuotationCreate.net_amount,
            },
          },
        });
      } catch (err) {
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(formatError || `${t("button.save_draft")}ไม่สำเร็จ`, {
          variant: "error",
        });
      }
    } else {
      try {
        const formatData = await quotationUpdatePayloadFormatter(data, "draft");
        const { QuotationUpdate } = await update({
          uniqueInput: {
            id: id ? parseInt(id) : undefined,
          },
          input: formatData as QuotationUpdateInput,
        });
        enqueueSnackbar(`${t("button.save_draft")}สำเร็จ`, {
          variant: "success",
        });

        await refetch();

        const formattedDirtyFields = Object.keys(dirtyFields);

        if (formattedDirtyFields.length > 0) {
          await createActivityLog({
            activity_type: ActivityType.Edit,
            document_type: ActivityLogDocumentType.Quotation,
            reference_id: QuotationUpdate?.id,
            activity_detail: {
              updated_fields: {
                net_amount:
                  netAmount?.toFixed(2) ===
                  QuotationUpdate.net_amount?.toFixed(2)
                    ? undefined
                    : QuotationUpdate.net_amount,
              },
            },
          });
        }
      } catch (err) {
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(formatError || `${t("button.save_draft")}ไม่สำเร็จ`, {
          variant: "error",
        });
      }
    }
  };

  const waitApproveHandler = async (data: IQuotation) => {
    if (!id) {
      try {
        const formatData = quotationCreatePayloadFormatter(
          data,
          "wait_approve",
          isApproval ? 1 : 0
        );
        const { QuotationCreate } = await create({
          input: formatData as QuotationCreateInput,
        });
        enqueueSnackbar(`${t("button.send") + t("button.approve")}สำเร็จ`, {
          variant: "success",
        });
        navigate(`/sales/quotation/${QuotationCreate.id}`);
        if (state && state.copied_id) {
          await createActivityLog({
            activity_type: ActivityType.Copy,
            document_type: ActivityLogDocumentType.Quotation,
            reference_id: QuotationCreate?.id,
            activity_detail: {
              copied_from: {
                id: state.copied_id,
                unique_id: state.copied_unique_id,
              },
              copied_to: {
                id: QuotationCreate.id,
                unique_id: QuotationCreate.unique_id,
              },
            },
          });
        }
        await createActivityLog({
          activity_type: ActivityType.StatusChange,
          document_type: ActivityLogDocumentType.Quotation,
          reference_id: QuotationCreate?.id,
          activity_detail: {
            secondary_operation: ActivityType.Create,
            curr_status: "wait_approve",
            updated_fields: {
              net_amount: QuotationCreate.net_amount,
            },
          },
        });
      } catch (err) {
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(
          formatError || `${t("button.send") + t("button.approve")}ไม่สำเร็จ`,
          {
            variant: "error",
          }
        );
      }
    } else {
      try {
        const formatData = await quotationUpdatePayloadFormatter(
          data,
          "wait_approve",
          isApproval ? 1 : 0
        );
        const { QuotationUpdate } = await update({
          uniqueInput: {
            id: id ? parseInt(id) : undefined,
          },
          input: formatData as QuotationUpdateInput,
        });
        await refetch();
        const formattedDirtyFields = Object.keys(dirtyFields);
        await createActivityLog({
          activity_type: ActivityType.StatusChange,
          document_type: ActivityLogDocumentType.Quotation,
          reference_id: QuotationUpdate?.id,
          activity_detail: {
            secondary_operation:
              formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
            prev_status: data.aggrid_status,
            curr_status: "wait_approve",
            updated_fields: {
              net_amount:
                netAmount?.toFixed(2) === QuotationUpdate.net_amount?.toFixed(2)
                  ? undefined
                  : QuotationUpdate.net_amount,
            },
          },
        });
      } catch (err) {
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(
          formatError || `${t("button.send") + t("button.approve")}ไม่สำเร็จ`,
          {
            variant: "error",
          }
        );
      }
    }
  };

  const approveHandler = async (data: IQuotation) => {
    if (!id) {
      //guarantee no approval
      try {
        const reviewed_by = {
          user_id: authUser?.id,
          first_name: authUser?.first_name || "",
          last_name: authUser?.last_name || "",
          reviewed_date: dayjs(),
        };

        const formatData = quotationCreatePayloadFormatter(
          data,
          "wait_accept",
          undefined,
          reviewed_by
        );

        const { QuotationCreate } = await create({
          input: formatData as QuotationCreateInput,
        });
        enqueueSnackbar(`${t("button.approve")}สำเร็จ`, {
          variant: "success",
        });
        navigate(`/sales/quotation/${QuotationCreate.id}`);
        if (state && state.copied_id) {
          await createActivityLog({
            activity_type: ActivityType.Copy,
            document_type: ActivityLogDocumentType.Quotation,
            reference_id: QuotationCreate?.id,
            activity_detail: {
              copied_from: {
                id: state.copied_id,
                unique_id: state.copied_unique_id,
              },
              copied_to: {
                id: QuotationCreate.id,
                unique_id: QuotationCreate.unique_id,
              },
            },
          });
        }
        await createActivityLog({
          activity_type: ActivityType.StatusChange,
          document_type: ActivityLogDocumentType.Quotation,
          reference_id: QuotationCreate?.id,
          activity_detail: {
            secondary_operation: ActivityType.Create,
            curr_status: "wait_accept",
            updated_fields: {
              net_amount: QuotationCreate.net_amount,
            },
          },
        });
      } catch (err) {
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(formatError || `${t("button.approve")}ไม่สำเร็จ`, {
          variant: "error",
        });
      }
    } else {
      try {
        let changingStatus = "wait_approve";
        let currentApprovalList: IApprovalTemplate[] = [];
        if (approvalStep && approvalStep > approvalTemplates.length - 1) {
          changingStatus = "wait_accept";
          currentApprovalList = approvalTemplates as IApprovalTemplate[];
        }

        if (!isApproval) {
          changingStatus = "wait_accept";
        }

        let currentApprovalStep = (approvalStep || 0) + 1;

        if (data.aggrid_status === "not_approved") {
          currentApprovalStep = approvalStep || 0;
        }
        const reviewed_by = {
          user_id: authUser?.id,
          first_name: authUser?.first_name || "",
          last_name: authUser?.last_name || "",
          reviewed_date: dayjs(),
        };

        const formatData = await quotationUpdatePayloadFormatter(
          data,
          changingStatus,
          currentApprovalStep,
          currentApprovalList,
          false,
          reviewed_by
        );
        const { QuotationUpdate } = await update({
          uniqueInput: {
            id: id ? parseInt(id) : undefined,
          },
          input: formatData as QuotationUpdateInput,
        });
        enqueueSnackbar(`${t("button.approve")}สำเร็จ`, {
          variant: "success",
        });

        await refetch();

        if (isApproval) {
          if (changingStatus === "wait_accept") {
            await createActivityLog({
              activity_type: ActivityType.StatusChange,
              document_type: ActivityLogDocumentType.Quotation,
              reference_id: QuotationUpdate?.id,
              activity_detail: {
                secondary_operation: ActivityType.Approve,
                message: [`ลำดับที่ ${approvalStep}`],
                prev_status: "wait_approve",
                curr_status: "wait_accept",
              },
            });
          } else {
            await createActivityLog({
              activity_type: ActivityType.Approve,
              document_type: ActivityLogDocumentType.Quotation,
              reference_id: QuotationUpdate?.id,
              activity_detail: {
                message: [`ลำดับที่ ${approvalStep}`],
              },
            });
          }
        } else {
          const formattedDirtyFields = Object.keys(dirtyFields);
          await createActivityLog({
            activity_type: ActivityType.StatusChange,
            document_type: ActivityLogDocumentType.Quotation,
            reference_id: QuotationUpdate?.id,
            activity_detail: {
              secondary_operation:
                formattedDirtyFields?.length > 0
                  ? ActivityType.Edit
                  : undefined,
              prev_status: data.aggrid_status,
              curr_status: "wait_accept",
              updated_fields: {
                net_amount:
                  netAmount?.toFixed(2) ===
                  QuotationUpdate.net_amount?.toFixed(2)
                    ? undefined
                    : QuotationUpdate.net_amount,
              },
            },
          });
        }
      } catch (err) {
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(formatError || `${t("button.approve")}ไม่สำเร็จ`, {
          variant: "error",
        });
      }
    }
  };

  const notApproveHandler = async (data: IQuotation) => {
    try {
      const flag_status =
        data?.flag_status && data.flag_status.length > 0
          ? data.flag_status.includes("not_approved")
            ? [...data.flag_status]
            : [...data.flag_status, "not_approved"]
          : ["not_approved"];

      const { QuotationUpdate } = await update({
        input: {
          item_price_list_id: data.item_price_list_id as number,
          flag_status: flag_status,
        },
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
      });

      await refetch();

      const formattedDirtyFields = Object.keys(dirtyFields);

      await createActivityLog({
        activity_type: ActivityType.StatusChange,
        document_type: ActivityLogDocumentType.Quotation,
        reference_id: QuotationUpdate?.id,
        activity_detail: {
          secondary_operation:
            formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
          prev_status: data.aggrid_status,
          curr_status: "not_approved",
          updated_fields:
            formattedDirtyFields?.length > 0 ? formattedDirtyFields : undefined,
        },
      });

      enqueueSnackbar(`${t("button.not_approve")}สำเร็จ`, {
        variant: "success",
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(`${t("button.not_approve")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const acceptHandler = async () => {
    try {
      trigger();
      if (isValid) {
        const data = getValues();
        const formatData = await quotationUpdatePayloadFormatter(
          data,
          "accepted",
          approvalStep,
          approverList
        );

        const { QuotationUpdate } = await update({
          uniqueInput: {
            id: id ? parseInt(id) : undefined,
          },
          input: formatData as QuotationUpdateInput,
        });
        enqueueSnackbar(`${t("button.accept")}สำเร็จ`, {
          variant: "success",
        });

        await refetch();

        const formattedDirtyFields = Object.keys(dirtyFields);
        await createActivityLog({
          activity_type: ActivityType.StatusChange,
          document_type: ActivityLogDocumentType.Quotation,
          reference_id: QuotationUpdate?.id,
          activity_detail: {
            secondary_operation:
              formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
            prev_status: data.aggrid_status,
            curr_status: "accepted",
            updated_fields:
              formattedDirtyFields?.length > 0
                ? formattedDirtyFields
                : undefined,
          },
        });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar(`${t("button.accept")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const {
    confirmation: acceptConfirmation,
    openConfirmationHandler: openAcceptConfirmation,
    closeConfirmationHandler: closeAcceptConfirmation,
    submitConfirmationHandler: submitAcceptConfirmation,
  } = useConfirmation(acceptHandler);

  const editClickHandler = () => {
    setDisabled(false);
    setIsEdit(true);
  };

  const cancelEditHandler = () => {
    setIsEdit(false);
    setDisabled(true);
    reset();
  };

  const editHandler = async (data: IQuotation, isNotApprove?: boolean) => {
    try {
      const formatData = await quotationUpdatePayloadFormatter(
        data,
        data?.main_status || "",
        approvalStep,
        approverList,
        isNotApprove
      );
      const { QuotationUpdate } = await update({
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
        input: formatData as QuotationUpdateInput,
      });

      enqueueSnackbar(`${t("sentence.edit")}สำเร็จ`, {
        variant: "success",
      });

      await refetch();

      const formattedDirtyFields = Object.keys(dirtyFields);

      if (formattedDirtyFields.length > 0) {
        await createActivityLog({
          activity_type: ActivityType.Edit,
          document_type: ActivityLogDocumentType.Quotation,
          reference_id: QuotationUpdate?.id,
          activity_detail: {
            secondary_operation:
              formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
            updated_fields: {
              net_amount:
                netAmount?.toFixed(2) === QuotationUpdate.net_amount?.toFixed(2)
                  ? undefined
                  : QuotationUpdate.net_amount,
            },
          },
        });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar(`${t("sentence.edit")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const cancelHandler = async () => {
    try {
      const data = getValues();
      const flag_status =
        data?.flag_status && data.flag_status.length > 0
          ? data.flag_status.includes("cancelled")
            ? [...data.flag_status]
            : [...data.flag_status, "cancelled"]
          : ["cancelled"];

      const { QuotationUpdate } = await update({
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
        input: {
          item_price_list_id: data.item_price_list_id as number,
          flag_status: flag_status,
          // approval_step: 0,
        },
      });

      enqueueSnackbar(`${t("button.cancel")}สำเร็จ`, {
        variant: "success",
      });

      await refetch();

      const formattedDirtyFields = Object.keys(dirtyFields);
      await createActivityLog({
        activity_type: ActivityType.StatusChange,
        document_type: ActivityLogDocumentType.Quotation,
        reference_id: QuotationUpdate?.id,
        activity_detail: {
          secondary_operation:
            formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
          prev_status: data.aggrid_status,
          curr_status: "cancelled",
          updated_fields:
            formattedDirtyFields?.length > 0 ? formattedDirtyFields : undefined,
        },
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(`${t("button.cancel")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const renderButton = (status: string | undefined) => {
    switch (status) {
      case "draft":
        return (
          <>
            <CustomizedButton
              variant="outlined"
              title={t("button.save_draft")}
              disabled={isCreating || isUpdating}
              onClick={handleSubmit(draftHandler)}
            />
            {isApproval ? (
              <CustomizedButton
                title={t("button.send")}
                variant="contained"
                disabled={isCreating || isUpdating}
                onClick={handleSubmit(waitApproveHandler)}
              />
            ) : (
              <CustomizedButton
                title={t("button.approve")}
                variant="contained"
                disabled={isCreating || isUpdating}
                onClick={handleSubmit(approveHandler)}
              />
            )}
          </>
        );
      case "wait_approve":
        if (isEdit) {
          return (
            <>
              <CustomizedButton
                variant="outlined"
                title={t("button.cancel")}
                onClick={cancelEditHandler}
                disabled={isUpdating}
              />
              <CustomizedButton
                title={t("button.save")}
                variant="contained"
                onClick={() => {
                  handleSubmit((data) => editHandler(data))();
                  setIsEdit(false);
                  setDisabled(true);
                }}
                disabled={isUpdating}
              />
            </>
          );
        } else {
          if (isHaveApproval && permissions?.quotation?.update) {
            return (
              <>
                <CustomizedButton
                  variant="outlined"
                  title={t("button.not_approve")}
                  disabled={isUpdating}
                  onClick={handleSubmit(notApproveHandler)}
                />
                <CustomizedButton
                  title={t("button.approve")}
                  variant="contained"
                  onClick={handleSubmit(approveHandler)}
                  disabled={isUpdating}
                />
              </>
            );
          } else {
            return;
          }
        }
      case "wait_accept":
        if (isEdit) {
          return (
            <>
              <CustomizedButton
                variant="outlined"
                title={t("button.cancel")}
                disabled={isUpdating}
                onClick={cancelEditHandler}
              />
              <CustomizedButton
                title={t("button.save")}
                variant="contained"
                onClick={() => {
                  handleSubmit((data) => editHandler(data))();
                  setIsEdit(false);
                  setDisabled(true);
                }}
                disabled={isUpdating}
              />
            </>
          );
        } else {
          return (
            <CustomizedButton
              title={t("button.accept")}
              variant="contained"
              onClick={openAcceptConfirmation}
            />
          );
        }
      case "not_approved":
        return (
          <>
            <CustomizedButton
              variant="outlined"
              title={t("button.save")}
              disabled={isUpdating}
              onClick={handleSubmit((data) => editHandler(data, true))}
            />
            <CustomizedButton
              title={t("button.send")}
              variant="contained"
              onClick={handleSubmit(waitApproveHandler)}
              disabled={isUpdating}
            />
          </>
        );
      case "accepted":
      case "cancelled":
      case "finished":
        if (isEdit) {
          return (
            <>
              <CustomizedButton
                variant="outlined"
                title={t("button.cancel")}
                disabled={isUpdating}
                onClick={cancelEditHandler}
              />
              <CustomizedButton
                title={t("button.save")}
                variant="contained"
                onClick={() => {
                  handleSubmit((data) => editHandler(data))();
                  setIsEdit(false);
                  setDisabled(true);
                }}
                disabled={isUpdating}
              />
            </>
          );
        }
        return;
      default:
        return (
          <>
            <CustomizedButton
              variant="outlined"
              title={t("button.save_draft")}
              disabled={isCreating || isUpdating}
              onClick={handleSubmit(draftHandler)}
            />
            {isApproval ? (
              <CustomizedButton
                title={t("button.send")}
                variant="contained"
                disabled={isCreating || isUpdating}
                onClick={handleSubmit(waitApproveHandler)}
              />
            ) : (
              <CustomizedButton
                title={t("button.approve")}
                variant="contained"
                disabled={isCreating || isUpdating}
                onClick={handleSubmit(approveHandler)}
              />
            )}
          </>
        );
    }
  };

  useSalesError(errors);

  if (isOptionLoading || (id && (isLoadingData || isLoading))) {
    return (
      <Box
        sx={{
          height: "calc(100dvh - 176px)",
          marginRight: "260px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <QuotationHeader
        editClickHandler={editClickHandler}
        cancelHandler={cancelHandler}
        approverList={approverList as IApprovalTemplate[]}
        approvalStep={approvalStep}
      />
      <CustomerInfo />
      <AddressInfo />
      <SalesInfo />
      <SalesItemList documentType="quotation" />
      {/* <Promotion /> */}
      <SalesFooter documentType="quotation" />
      {status === "accepted" && (
        <CustomizedBox margin={0}>
          <AcceptQuotationForm />
        </CustomizedBox>
      )}
      <BottomNavbar>
        {(!disabled ||
          status === "wait_approve" ||
          status === "wait_accept") && (
          <Stack direction="row" spacing={1} alignItems="center">
            {renderButton(status)}
          </Stack>
        )}
      </BottomNavbar>
      <ModalUI
        title="ยอมรับใบเสนอราคา"
        open={acceptConfirmation}
        handleClose={closeAcceptConfirmation}
        action={
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "flex-end",
              gap: ".5rem",
            }}
          >
            <CustomizedButton
              title="ยกเลิก"
              variant="outlined"
              onClick={closeAcceptConfirmation}
            />
            <CustomizedButton
              title="บันทึก"
              variant="contained"
              onClick={submitAcceptConfirmation}
            />
          </Box>
        }
      >
        <AcceptQuotationForm isModal />
      </ModalUI>
    </>
  );
};

export default DocumentInfoTab;
